import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Pagination from '../../components/common/Pagination';
import FollowButton from '../../components/seller/FollowButton';
import { useFollow } from '../../hooks/useFollow';
import { theme } from '../../config/theme';
import { UsersIcon } from '../../components/icons/Icons';
import { formatDate } from '../../utils/helpers';

const Following = () => {
  const navigate = useNavigate();
  const { following, pagination, loading, fetchFollowing } = useFollow();

  useEffect(() => {
    fetchFollowing({ page: 1 });
  }, [fetchFollowing]);

  const handlePageChange = (page) => fetchFollowing({ page });

  if (loading && following.length === 0) return <LoadingSpinner fullScreen />;

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: theme.spacing.xl }}>
          <h1 style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            marginBottom: theme.spacing.xs,
          }}>
            Following
          </h1>
          <p style={{ color: theme.colors.text.secondary }}>
            {pagination?.total ?? 0} seller{pagination?.total !== 1 ? 's' : ''} you follow
          </p>
        </div>

        {following.length === 0 ? (
          <EmptyState
            icon={<UsersIcon size={48} />}
            title="Not following anyone yet"
            description="Browse products and follow sellers you like"
            action={
              <Button variant="primary" onClick={() => navigate('/products')}>
                Browse Products
              </Button>
            }
          />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              {following.map((item) => {
                const seller = item.seller;
                return (
                  <Card key={item.id} padding="lg">
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: theme.spacing.md,
                    }}>
                      {/* Clickable seller info */}
                      <div
                        onClick={() => navigate(`/sellers/${seller?.id}`)}
                        style={{
                          flex: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          fontWeight: theme.typography.fontWeight.semibold,
                          fontSize: theme.typography.fontSize.base,
                          marginBottom: theme.spacing.xs,
                          color: theme.colors.text.primary,
                          transition: theme.transitions.fast,
                        }}
                          onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primary.main}
                          onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.text.primary}
                        >
                          {seller?.store_name ?? 'Unknown Store'}
                        </div>
                        <div style={{
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.colors.text.secondary,
                        }}>
                          {seller?.user?.first_name} {seller?.user?.last_name}
                        </div>
                        <div style={{
                          fontSize: theme.typography.fontSize.xs,
                          color: theme.colors.text.disabled,
                          marginTop: theme.spacing.xs,
                        }}>
                          Following since {formatDate(item.created_at)}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/sellers/${seller?.id}`)}
                          style={{ color: theme.colors.primary.main, fontSize: theme.typography.fontSize.sm }}
                        >
                          View Store →
                        </Button>
                        <FollowButton sellerId={seller?.id} size="sm" />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div style={{ marginTop: theme.spacing.xl }}>
              <Pagination
                currentPage={pagination?.page}
                totalPages={pagination?.totalPages}
                hasNextPage={pagination?.hasNextPage}
                hasPreviousPage={pagination?.hasPreviousPage}
                totalItems={pagination?.total}
                itemsPerPage={pagination?.limit}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Following;